import { useTranslation } from "react-i18next";
import {
  JsonSchemaProperty,
  RenderObjectProps,
  RowData,
} from "../../../../types";
import {
  IconDeleteWrapper,
  RecordPageFieldLayout,
  RecordPageFormLayout,
  RecordPageSectionLayout,
  RecordSectionTitle,
} from "../../../../utils/styled";
import { SectionInfoIconWithTooltip } from "../../../common/Tooltip";
import { getSectionTooltipInfo, getTitleFromKey } from "../../helpers";
import { resolveCustomStyle } from "../../style-config";
import i18n from "../../../../translate/i18next";
import { FormattedMessage } from "react-intl";
import {
  IconAddCircle,
  IconButton,
  IconDelete,
  TextButton,
} from "@cdkglobal/radial";
import { DynamicAdvancedFieldRenderer } from "./DynamicAdvancedFieldRenderer";

export default function RenderObjects({
  propertyKey,
  property,
  dependencyRules,
  triggerDependencyRule,
  selectedTabSectionKey,
  rowDataReducerState,
  dispatch,
}: Readonly<RenderObjectProps>): JSX.Element | null {
  const { schema, isReadOnly, rowData } = rowDataReducerState;
  const { t } = useTranslation();

  const { properties = {}, required = [] } = property;
  const inputValueObject = (rowData[propertyKey] as RowData) ?? {};

  // TODO: we may also want to check for min/max length of the array and disable add/delete buttons accordingly
  const shouldShowDeleteBtn = property.default === null;
  const shouldShowAddBtn =
    shouldShowDeleteBtn && Object.keys(inputValueObject).length === 0;

  const handleAddNestedObject = () => {
    dispatch({
      type: "ADD_SINGLE_OBJECT",
      payload: {
        propertyKey,
      },
    });
  };

  const handleDeleteNestedObject = () => {
    dispatch({
      type: "DELETE_SINGLE_OBJECT",
      payload: {
        propertyKey,
      },
    });
  };

  const renderNestedFields = () =>
    Object.keys(properties).map((propertySubKey) => {
      const { isHidden } = properties[propertySubKey] as JsonSchemaProperty;

      return (
        <RecordPageFieldLayout
          key={propertySubKey}
          $customStyle={resolveCustomStyle({
            applicability: "field",
            isHidden,
            propertyKey: propertySubKey,
          })}
        >
          <DynamicAdvancedFieldRenderer
            propertyKey={propertyKey}
            properties={properties}
            required={required}
            rowScopedProperties={undefined}
            inputValueObject={inputValueObject}
            dependencyRules={dependencyRules}
            triggerDependencyRule={triggerDependencyRule}
            rowIndex={undefined}
            propertySubKey={propertySubKey}
            itemProperty={properties[propertySubKey]}
            isDialogField={false}
          />
        </RecordPageFieldLayout>
      );
    });

  const renderDeleteButton = () => {
    if (!shouldShowDeleteBtn) {
      return null;
    }

    return (
      <IconDeleteWrapper>
        <IconButton
          icon={<IconDelete />}
          dataTestId={`delete-${propertyKey}-row-button`}
          text={<FormattedMessage id="button.delete" defaultMessage="Delete" />}
          onClick={handleDeleteNestedObject}
          style={{ cursor: "pointer" }}
          isDisabled={isReadOnly}
        />
      </IconDeleteWrapper>
    );
  };

  const renderContent = () => {
    if (shouldShowAddBtn) {
      return (
        <TextButton
          icon={<IconAddCircle />}
          dataTestId={`add-${propertyKey}-row-button`}
          style={{ marginBottom: "1rem", width: "fit-content" }}
          onClick={handleAddNestedObject}
          text={<FormattedMessage id="button.add" defaultMessage="Add" />}
          isDisabled={isReadOnly}
        />
      );
    }

    return (
      <>
        {renderNestedFields()}
        {renderDeleteButton()}
      </>
    );
  };


  return (
    <RecordPageSectionLayout>
      <SectionInfoIconWithTooltip
        tooltip={getSectionTooltipInfo({
          selectedTabSectionKey,
          sectionKey: propertyKey,
          schema,
        })}
      />

      <RecordSectionTitle>
        {getTitleFromKey(propertyKey, t, i18n.language)}
      </RecordSectionTitle>

      <RecordPageFormLayout>{renderContent()}</RecordPageFormLayout>
    </RecordPageSectionLayout>
  );
}
